import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { PermissionKey } from '@csa/shared';
import { api } from '../lib/api.js';

/**
 * The server's view of the signed-in user.
 *
 * Distinct from `useAuth`, which only knows what Entra said. Authentication
 * proves who somebody is; this call establishes what they are allowed to do,
 * and the two are deliberately not conflated -- a user can be perfectly
 * authenticated and still hold no permissions at all, which is exactly the
 * state a newly provisioned account is in.
 */

export interface SessionUser {
  readonly oid: string;
  readonly upn: string;
  readonly displayName: string;
  readonly userId: string;
}

export interface EntitlementRoleView {
  readonly roleId: string;
  readonly name: string;
  readonly source: 'direct' | 'entra-group' | 'breakglass';
  readonly expiresAt: string | null;
}

export interface TargetEntitlement {
  readonly targetId: string;
  readonly targetKey: string;
  readonly permissions: readonly PermissionKey[];
}

export interface EntitlementsView {
  readonly roles: readonly EntitlementRoleView[];
  /** Permissions held without target scoping. Privileged keys never appear here. */
  readonly global: readonly PermissionKey[];
  readonly byTarget: readonly TargetEntitlement[];
  /** Union of global and per-target. Drives coarse navigation only. */
  readonly all: readonly PermissionKey[];
  readonly resolvedAt: string;
}

export interface SessionResponse {
  readonly user: SessionUser;
  readonly session: { readonly id: string };
  readonly entitlements: EntitlementsView;
  readonly isBootstrapAdmin: boolean;
  readonly preferences: SessionPreferences;
}

export interface SessionPreferences {
  /**
   * IANA zone name, or `null` to follow the browser -- which is the normal
   * state, and why this is nullable rather than defaulted to a zone on the
   * server. The server has no idea where the reader is sitting; the browser
   * does.
   */
  readonly timeZone: string | null;
}

export const SESSION_QUERY_KEY = ['session'] as const;

export function useSession(enabled: boolean): UseQueryResult<SessionResponse, Error> {
  return useQuery({
    queryKey: SESSION_QUERY_KEY,
    // The signin header is what provisions the user row and opens the session
    // record. Sending it only here keeps that write off every other request.
    queryFn: () => api.get<SessionResponse>('/api/me', { signin: true }),
    enabled,
    // Entitlements change when an administrator edits a role, and the user
    // should not have to reload to lose access they just had revoked.
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    retry: 1,
  });
}

/**
 * Client-side permission checks, for hiding what a user cannot use.
 *
 * Presentation only. Every one of these is enforced again on the server, and
 * nothing here is load-bearing: a user who edits the response in a debugger
 * gets a visible button and a 403 when they press it.
 */
export function canAnywhere(
  entitlements: EntitlementsView | undefined,
  permission: PermissionKey,
): boolean {
  return entitlements?.all.includes(permission) ?? false;
}

export function canOnTarget(
  entitlements: EntitlementsView | undefined,
  permission: PermissionKey,
  targetId: string,
): boolean {
  const target = entitlements?.byTarget.find((t) => t.targetId === targetId);
  return target?.permissions.includes(permission) ?? false;
}

/**
 * The roles the signed-in user holds.
 *
 * Reads the cached session rather than taking it as a prop, because the one
 * caller -- the script library's share dialog -- is three components below
 * anything that has it, and threading it through would make two unrelated
 * components carry a prop they do not use.
 *
 * Presentation only, like everything else here. The publish route checks
 * role membership itself and refuses a role the caller does not hold.
 */
export function useSessionRoles(): readonly EntitlementRoleView[] {
  const { data } = useQuery<SessionResponse>({ queryKey: SESSION_QUERY_KEY, enabled: false });
  return data?.entitlements.roles ?? [];
}

/** Targets on which the user holds `permission`. Drives target pickers. */
export function targetsWith(
  entitlements: EntitlementsView | undefined,
  permission: PermissionKey,
): readonly TargetEntitlement[] {
  return entitlements?.byTarget.filter((t) => t.permissions.includes(permission)) ?? [];
}
